﻿/*
 * Purpose: Compose OTP input with request/resend logic and validation.
 * Boundary: Component only; connects hook state to layout; no side effects beyond auth service.
 * Invariants:
 * - Validates only on button click.
 * - Cooldown enforced after request or resend.
 * - Exposes isValid and otpValue via context setters.
 * - ASCII only; no Unicode in comments or strings.
 */

// Imports
import { createContext, useContext, useEffect } from 'react';
import OtpLayout from './otp/otp-layout';
import { useOtp } from './otp/otp-hooks';

// Context for sharing OTP state upward
export const OtpContext = createContext();

// Component Otp:
// Headless wrapper connecting useOtp state to OtpLayout; updates parent via context
const Otp = ({ auth, idNumber, cellNumber, isProceedReadyOtp, otpLength, channel, resetSignal }) => {

    // State Management - initialize form and handlers from hook
    const props = useOtp({ auth, idNumber, cellNumber, isProceedReadyOtp, otpLength, channel, resetSignal });

    // Context - get setter from parent
    const { setOtpState } = useContext(OtpContext) || {};

    // Effect: Update parent via context
    useEffect(() => {
        if (setOtpState) {
            setOtpState({ 
                isValid: props.isValid, 
                otpValue: props.otpValue,
                hasRequested: props.hasRequested,
                cooldown: props.cooldown
            });
        }
    }, [props.isValid, props.otpValue, props.hasRequested, props.cooldown, setOtpState]);

    // Rendering - delegate structure to layout
    return <OtpLayout {...props} />;
};

// Component metadata - support React DevTools naming
Otp.displayName = 'Otp';

// Export - default for use within registration flow
export default Otp;